import { action, observable } from 'mobx';
import { BaseViewStore, } from 'core/stores';
import upperFirst from "lodash/upperFirst";
import { NikoPatientDetailService, NikoPatientDetailInsuranceService, NikoPatientTemplateService } from "application/niko-patient/services";
import { ModalParams } from "core/models";

class NikoPatientInsurancePreviewViewStore extends BaseViewStore {
    @observable accessor patientInsurance;
    @observable accessor nikoPatientDetail;
    disabled = false;
    @observable accessor showAddPrimaryActive = false;
    @observable accessor showAddPrimaryInactive = false;
    @observable accessor showAddSecondaryActive = false;
    @observable accessor showAddSecondaryInactive = false;
    @observable accessor showChangePrimaryActive = false;
    @observable accessor showChangeSecondaryActive = false;
    @observable accessor showEditPrimaryActive = false;
    @observable accessor showEditPrimaryInactive = false;
    @observable accessor showEditSecondaryActive = false;
    @observable accessor showEditSecondaryInactive = false;
    @observable accessor showDeletePrimaryActive = false;
    @observable accessor showDeletePrimaryInactive = false;
    @observable accessor showDeleteSecondaryActive = false;
    @observable accessor showDeleteSecondaryInactive = false;
    @observable accessor showInactivatePrimaryActive = false;
    @observable accessor showInactivatePrimaryInactive = false;
    @observable accessor showInactivateSecondaryActive = false;
    @observable accessor showInactivateSecondaryInactive = false;
    @observable accessor showDeleteAllowedPrimaryActive = false;
    @observable accessor showDeleteAllowedPrimaryInactive = false;
    @observable accessor showDeleteAllowedSecondaryActive = false;
    @observable accessor showDeleteAllowedSecondaryInactive = false;
    @observable accessor showEditInsurancePolicy = false;
    @observable accessor disabled = false;
    @observable accessor loading = true;

    constructor(rootStore, componentsProps) {
        super(rootStore);

        this.rootStore = rootStore;
        this.nikoPatientDetail = componentsProps.nikoPatientDetail;
        this.getNikoPatient = componentsProps.getNikoPatient;
        this.sendOrder = componentsProps.sendOrder;
        this.nikoPatientDetailService = new NikoPatientDetailService(this.rootStore.application.baasic.apiClient);
        this.nikoPatientDetailInsuranceService = new NikoPatientDetailInsuranceService(this.rootStore.application.baasic.apiClient);
        this.nikoPatientTemplateService = new NikoPatientTemplateService(this.rootStore.application.baasic.apiClient);
        this.resetPARFlags = componentsProps.resetPARFlags;

        this.insuranceChangeConfirmModal = new ModalParams({});
        this.insuranceChangeConfirmModal.yesLabel = 'Confirm';
        this.insuranceChangeConfirmModal.onCancel = () => { this.insuranceChangeConfirmModal.close() };
        this.insuranceChangeConfirmModal.noLabel = 'Cancel';
        this.insuranceChangeConfirmModal.swapButtons = true;

        this.fetch([
            this.initialize()
        ]);
    }

    @action.bound
    async initialize(getData = false) {
        this.loading = true;
        if (getData) {
            if((this.showAddPrimaryActive || this.showChangePrimaryActive || this.showDeletePrimaryActive) && this.resetPARFlags){
                this.resetPARFlags();
            }
            this.closeAll();
            await this.getNikoPatient();
            const response = await this.nikoPatientDetailService.get(this.nikoPatientDetail.id, { fields: "NikoPatientDetailInsurances", embed: "NikoPatientDetailInsurances" });
            this.nikoPatientDetail.nikoPatientDetailInsurances = response.data.nikoPatientDetailInsurances;
        }

        this.patientInsurance = {
            nikoPatientDetailId: this.nikoPatientDetail.id,
            primaryActiveId: null, primaryActiveInsuranceId: null, primaryActiveInsurance: null, primaryActivePolicyNo: null, primaryActiveStartDate: null, primaryActiveEndDate: null, primaryActiveRelationship: null, primaryActiveInsuredFirstName: null, primaryActiveInsuredLastName: null, primaryActiveInsuredDOB: null, primaryActiveInsuredGender: null,
            primaryInactiveId: null, primaryInactiveInsuranceId: null, primaryInactiveInsurance: null, primaryInactivePolicyNo: null, primaryInactiveStartDate: null, primaryInactiveEndDate: null, primaryInactiveRelationship: null, primaryInactiveInsuredFirstName: null, primaryInactiveInsuredLastName: null, primaryInactiveInsuredDOB: null, primaryInactiveInsuredGender: null,
            secondaryActiveId: null, secondaryActiveInsuranceId: null, secondaryActiveInsurance: null, secondaryActivePolicyNo: null, secondaryActiveStartDate: null, secondaryActiveEndDate: null, secondaryActiveRelationship: null, secondaryActiveInsuredFirstName: null, secondaryActiveInsuredLastName: null, secondaryActiveInsuredDOB: null, secondaryActiveInsuredGender: null,
            secondaryInactiveId: null, secondaryInactiveInsuranceId: null, secondaryInactiveInsurance: null, secondaryInactivePolicyNo: null, secondaryInactiveStartDate: null, secondaryInactiveEndDate: null, secondaryInactiveRelationship: null, secondaryInactiveInsuredFirstName: null, secondaryInactiveInsuredLastName: null, secondaryInactiveInsuredDOB: null, secondaryInactiveInsuredGender: null
        };

        if (this.nikoPatientDetail.nikoPatientDetailInsurances && this.nikoPatientDetail.nikoPatientDetailInsurances.length > 0) {
            this.nikoPatientDetail.nikoPatientDetailInsurances.forEach(i => {
                const keyPrefix = `${i.type.toLowerCase()}${i.status}`;
                this.patientInsurance[`${keyPrefix}Insurance`] = i.insurance;
                this.patientInsurance[`${keyPrefix}InsuranceId`] = i.insuranceId;
                this.patientInsurance[`${keyPrefix}Id`] = i.id;
                this.patientInsurance[`${keyPrefix}PolicyNo`] = i.policyNo;
                this.patientInsurance[`${keyPrefix}StartDate`] = i.startDate;
                this.patientInsurance[`${keyPrefix}EndDate`] = i.endDate;
                this.patientInsurance[`${keyPrefix}Relationship`] = i.relationship;
                this.patientInsurance[`${keyPrefix}InsuredFirstName`] = i.insuredFirstName;
                this.patientInsurance[`${keyPrefix}InsuredLastName`] = i.insuredLastName;
                this.patientInsurance[`${keyPrefix}InsuredDOB`] = i.insuredDOB;
                this.patientInsurance[`${keyPrefix}InsuredGender`] = i.insuredGender;
            });
        }

        this.computeDeleteAllowedFlags();
        this.loading = false;
    }

    @action.bound
    computeDeleteAllowedFlags() {
        const initialInsurances = this.nikoPatientDetail.initialNikoPatientDetail && this.nikoPatientDetail.initialNikoPatientDetail.NikoPatientDetailInsurances;
        const sendOrderAllowsDelete = !this.nikoPatientDetail.facilityOrderId || this.sendOrder === false;

        ['primaryActive', 'primaryInactive', 'secondaryActive', 'secondaryInactive'].forEach(keyPrefix => {
            const currentId = this.patientInsurance[`${keyPrefix}Id`];
            const currentInsuranceId = this.patientInsurance[`${keyPrefix}InsuranceId`];
            const flagKey = `showDeleteAllowed${upperFirst(keyPrefix)}`;

            if (!currentId) {
                this[flagKey] = false;
                return;
            }

            const initialMatch = initialInsurances && initialInsurances.find(i => `${i.Type.toLowerCase()}${i.Status}` === keyPrefix);
            const addedOrChangedOnOrder = !initialMatch || initialMatch.InsuranceId !== currentInsuranceId;

            this[flagKey] = addedOrChangedOnOrder && sendOrderAllowsDelete;
        });
    }

    @action.bound
    closeAll() {
        this.showAddPrimaryActive = false;
        this.showAddPrimaryInactive = false;
        this.showAddSecondaryActive = false;
        this.showAddSecondaryInactive = false;
        this.showChangePrimaryActive = false;
        this.showChangeSecondaryActive = false;
        this.showEditPrimaryActive = false;
        this.showEditPrimaryInactive = false;
        this.showEditSecondaryActive = false;
        this.showEditSecondaryInactive = false;
        this.showDeletePrimaryActive = false;
        this.showDeletePrimaryInactive = false;
        this.showDeleteSecondaryActive = false;
        this.showDeleteSecondaryInactive = false;
        this.showInactivatePrimaryActive = false;
        this.showInactivatePrimaryInactive = false;
        this.showInactivateSecondaryActive = false;
        this.showInactivateSecondaryInactive = false;
        this.showEditInsurancePolicy = false;
    }

    @action.bound
    openAddCard(keyPrefix) {
        this.closeAll();
        this[`showAdd${upperFirst(keyPrefix)}`] = true;
    }
    @action.bound
    openEditCard(keyPrefix) {
        this.closeAll();
        this[`showEdit${upperFirst(keyPrefix)}`] = true;
    }

    @action.bound
    openDeleteCard(keyPrefix) {
        this.closeAll();
        this[`showDelete${upperFirst(keyPrefix)}`] = true;
    }

    @action.bound
    openInactivateCard(keyPrefix) {
        this.closeAll();
        this[`showInactivate${upperFirst(keyPrefix)}`] = true;
    }

    @action.bound
    openChangeCard(keyPrefix) {
        this.closeAll();
        this[`showChange${upperFirst(keyPrefix)}`] = true;
    }

    @action.bound
    confirmEditInsurancePolicy() {
        this.showEditInsurancePolicy = true;
    }

    @action.bound
    async deleteInsurancePolicy(keyPrefix) {
        if (keyPrefix == 'primaryActive' && this.nikoPatientDetail.eligibilityStatus && this.nikoPatientDetail.eligibilityStatus.name === "Eligible") {
            let nikoPatientTemplates = (await this.nikoPatientTemplateService.find({ nikoPatientDetailId: this.nikoPatientDetail.id, facilityOrderId: this.nikoPatientDetail.facilityOrderId, embed: "ClinicalStatus", fileds: "ClinicalStatus", })).data.item;
            if (nikoPatientTemplates.some(x => x.clinicalStatus && (x.clinicalStatus.name === "Prior Auth Obtained" || x.clinicalStatus.name === "Prior Auth Required"))) {
                this.insuranceChangeConfirmModal.onConfirm = async () => {
                    this.insuranceChangeConfirmModal.loading = true;
                    this.deleteInsurance(keyPrefix);
                    this.insuranceChangeConfirmModal.loading = false;
                    this.insuranceChangeConfirmModal.close();
                };
                this.insuranceChangeConfirmModal.open({
                    message: "After the patient's primary active insurance gets changed, the PARs will be removed from all of the patient’s templates."
                });
                return;
            }
        }
        
        this.deleteInsurance(keyPrefix);
    }

    @action.bound
    async deleteInsurance(keyPrefix) {
        this.disabled = true;
        try {
            await this.nikoPatientDetailInsuranceService.delete({ id: this.patientInsurance[`${keyPrefix}Id`] });
        }
        catch (err) {
            this.rootStore.notificationStore.error(err && err.data ? err.data : "Error occured while trying to remove insurance.");
        }
        finally {
            await this.initialize(true);
            this.disabled = false;
        }
    }

    @action.bound
    async inactivateInsurancePolicy(keyPrefix, endDate, endDateUnavailable) {
        if (!endDateUnavailable && !endDate) {
            this.rootStore.notificationStore.error("Enter an end date or check End Date Unavailable.");
            return;
        }

        const startDate = this.patientInsurance[`${keyPrefix}StartDate`];
        if (!endDateUnavailable && startDate && endDate && new Date(endDate) <= new Date(startDate)) {
            this.rootStore.notificationStore.error("End Date must be greater than Start Date.");
            return;
        }

        const resolvedEndDate = endDateUnavailable ? null : endDate;

        if (keyPrefix == 'primaryActive' && this.nikoPatientDetail.eligibilityStatus && this.nikoPatientDetail.eligibilityStatus.name === "Eligible") {
            let nikoPatientTemplates = (await this.nikoPatientTemplateService.find({ nikoPatientDetailId: this.nikoPatientDetail.id, facilityOrderId: this.nikoPatientDetail.facilityOrderId, embed: "ClinicalStatus", fileds: "ClinicalStatus", })).data.item;
            if (nikoPatientTemplates.some(x => x.clinicalStatus && (x.clinicalStatus.name === "Prior Auth Obtained" || x.clinicalStatus.name === "Prior Auth Required"))) {
                this.insuranceChangeConfirmModal.onConfirm = async () => {
                    this.insuranceChangeConfirmModal.loading = true;
                    await this.inactivateInsurance(keyPrefix, resolvedEndDate);
                    this.insuranceChangeConfirmModal.loading = false;
                    this.insuranceChangeConfirmModal.close();
                };
                this.insuranceChangeConfirmModal.open({
                    message: "After the patient's primary active insurance gets changed, the PARs will be removed from all of the patient’s templates."
                });
                return;
            }
        }

        await this.inactivateInsurance(keyPrefix, resolvedEndDate);
    }

    @action.bound
    async inactivateInsurance(keyPrefix, endDate) {
        this.disabled = true;
        try {
            await this.nikoPatientDetailInsuranceService.inactivate({ id: this.patientInsurance[`${keyPrefix}Id`], endDate: endDate });
        }
        catch (err) {
            this.rootStore.notificationStore.error(err && err.data ? err.data : "Error occured while trying to inactivate insurance.");
        }
        finally {
            await this.initialize(true);
            this.disabled = false;
        }
    }
}

export default NikoPatientInsurancePreviewViewStore;
